import type { ChatMessage } from "~/components/AiChat";
import api from "./api";
import { handleTokenLimitError } from "~/lib/utils";

const API_URL = import.meta.env.VITE_API_URL;

const sanitizeInput = (input: string): string => {
  // Removes potentially dangerous characters and encodes special characters
  return input
    .replace(/[<>]/g, "") // Removes HTML tags
    .replace(/"/g, "&quot;") // Encodes double quotes
    .replace(/'/g, "&apos;") // Encodes single quotes
    .replace(/\\/g, "\\\\") // Escapes backslashes
    .replace(/\n/g, "\\n"); //
};

export const getProject = async (
  projectId: string,
  userId: string | null,
  wsConnectionId: string = "",
) => {
  try {
    const payload = {
      projectId: projectId,
      userId: userId,
      wsConnectionId: wsConnectionId,
    };
    const response = await api.post(
      `${API_URL}/kodesherpa/getProject`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching project:", error);
    throw error;
  }
};

export const getTemplates = async (page: number = 1, limit: number = 20) => {
  try {
    const payload = {
      page: page,
      limit: limit,
    };
    const response = await api.post(
      `${API_URL}/kodesherpa/template/get-templates`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching templates:", error);
    throw error;
  }
};

export const duplicateProject = async (projectId: string) => {
  try {
    const response = await api.post(`${API_URL}/kodesherpa/duplicateProject`, {
      projectId,
    });
    return response.data;
  } catch (error) {
    console.error("Error duplicating project:", error);
    throw error;
  }
};

export const openTemplate = async (templateId: string) => {
  try {
    const response = await api.post(`${API_URL}/kodesherpa/template/open`, {
      templateId,
    });
    return response.data;
  } catch (error) {
    console.error("Error opening template:", error);
    throw error;
  }
};

export const getTemplatePreview = async (templateId: string) => {
  try {
    const response = await api.post(`${API_URL}/kodesherpa/template/preview`, {
      templateId,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching template preview:", error);
    throw error;
  }
};

export const downloadProject = async (
  chatId: string,
  retrieveTestsFromDb: boolean,
  simpleTest: boolean = false,
) => {
  try {
    const payload = {
      chatId: chatId,
      retrieveTestsFromDb: retrieveTestsFromDb,
      simpleTest: simpleTest,
    };
    const response = await api.post(
      `${API_URL}/kodesherpa/downloadHardhatProject`,
      payload,
    );

    return response.data;
  } catch (error: any) {
    // Note: Backend only checks tokens when generating new tests (retrieveTestsFromDb = false)
    // But we should still handle token limit errors if they occur
    const handledError = handleTokenLimitError(error);
    if (handledError !== error) {
      throw handledError;
    }
    // console.error("Error posting message:", error);
    throw error;
  }
};

export const loadLatestCodeGeneration = async (historyId: number) => {
  try {
    const response = await api.get(
      `${API_URL}/kodesherpa/loadLatestCodeGeneration/` + historyId,
    );
    return response.data;
  } catch (error) {
    console.error("Error loading latest code generation:", error);
    throw error;
  }
};

export const loadGeneratedCodeList = async (
  historyId: number,
  page: number,
) => {
  try {
    const payload = {
      historyId: historyId,
      before: new Date().toISOString(),
      page: page,
      pageSize: 1,
    };
    const response = await api.post(
      `${API_URL}/kodesherpa/loadGenerations`,
      payload,
    );
    return response.data;
  } catch (error) {
    // console.error("Error posting message:", error);
    throw error;
  }
};

export const loadMessagesByHistoryId = async (
  historyId: number,
  page: number,
) => {
  try {
    const payload = {
      historyId: historyId,
      before: new Date().toISOString(),
      page: page,
      pageSize: 100,
    };
    const response = await api.post(
      `${API_URL}/kodesherpa/loadMessages`,
      payload,
    );
    return response.data;
  } catch (error) {
    // console.error("Error posting message:", error);
    throw error;
  }
};

export const loadHistory = async (page: number) => {
  try {
    // Backend expects: userId (from JWT), page (number), pageSize (number)
    // Ensure page and pageSize are numbers, not strings
    const payload = {
      page: Number(page) || 1,
      pageSize: 15,
    };
    const response = await api.post(
      `${API_URL}/kodesherpa/loadHistory`,
      payload,
    );
    return response.data;
  } catch (error) {
    // console.error("Error posting message:", error);
    throw error;
  }
};

export const generateSolidity = async (
  contractDescription: string,
  wsConnectionId: string,
  chatId: string,
) => {
  const payload = {
    projectName: "Test",
    contractDescription: contractDescription,
    wsConnectionId: wsConnectionId,
    chatId: chatId,
  };
  try {
    const response = await api.post(`${API_URL}/generateSolidity`, payload);
    return response.data;
  } catch (error: any) {
    const handledError = handleTokenLimitError(error);
    if (handledError !== error) {
      throw handledError;
    }
    throw error;
  }
};

export const callGptMemory = async (
  messagesMemory: ChatMessage[],
  message: string,
  chatId: string,
  wsConnectionId: string,
) => {
  try {
    const sanitizedMessage = sanitizeInput(message);
    const response = await api.post(`${API_URL}/kodesherpa/chat`, {
      message: sanitizedMessage,
      chatId: chatId,
      wsConnectionId: wsConnectionId,
    });
    return response.data;
  } catch (error: any) {
    const handledError = handleTokenLimitError(error);
    if (handledError !== error) {
      throw handledError;
    }
    throw error;
  }
};

export const updateCRM = async (updateCRM: {
  caller: string;
  action: string;
  properties?: { label: string; value: any }[];
}) => {
  try {
    const response = await api.post(
      `${API_URL}/kodesherpa/crm/update-for-service`,
      updateCRM,
    );
    return response.data;
  } catch (error) {
    console.error("Error posting message:", error);
    throw error;
  }
};

export const compileSolidity = async (
  wsConnectionId: string,
  chatId: string,
) => {
  try {
    // Create the payload
    const payload = {
      wsConnectionId,
      chatId,
    };

    const response = await api.post(
      `${API_URL}/kodesherpa/compileSol`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error compiling Solidity code:", error);
    throw error;
  }
};

export const fixErrors = async (
  wsConnectionId: string,
  chatId: string,
  maxRetries: number = 3,
) => {
  try {
    const response = await api.post(`${API_URL}/kodesherpa/fixErrors`, {
      wsConnectionId,
      chatId,
      maxRetries,
    });
    return response.data;
  } catch (error: any) {
    const handledError = handleTokenLimitError(error);
    if (handledError !== error) {
      throw handledError;
    }
    console.error("Error calling fixErrors API:", error);
    throw error;
  }
};

/** File item for create: path (folder path), type file|folder, optional content for files */
export interface HandleFileCreate {
  path: string;
  type: "file" | "folder";
  content?: string;
}

/** File item for update: path or nodeId, new content */
export interface HandleFileUpdate {
  path?: string;
  nodeId?: number | string;
  newName?: string | null;
  newPath?: string | null;
  newContent?: string | null;
}
/** File item for delete: path or nodeId */
export interface HandleFileDelete {
  path?: string;
  nodeId?: number | string;
}

export type HandleFileItem =
  | HandleFileCreate
  | HandleFileUpdate
  | HandleFileDelete;

/** Result for a single processed file */
export interface HandleFileResult {
  path: string;
  nodeId?: number | string;
  success: boolean;
  error?: string;
  /** For delete of folder: ids and paths of files/folders removed with it */
  deletedWith?: { nodeId: number | string; path: string; type: string }[];
}

export interface HandleFilesResponse {
  results: HandleFileResult[];
  /** When deleting folders, total count of nodes deleted (folder + descendants) */
  foldersDeletedWithCount?: number;
}

export const handleFiles = async (payload: {
  projectId: string | number;
  operation: "create" | "update" | "rename" | "delete";
  files: HandleFileItem[];
}): Promise<HandleFilesResponse | undefined> => {
  try {
    console.log("handleFiles", payload);
    const response = await api.post(
      `${API_URL}/kodesherpa/handleFiles`,
      payload,
    );
    if (response.status !== 200) {
      throw new Error(`handleFiles failed with HTTP status ${response.status}`);
    }
    return response.data;
  } catch (error: any) {
    console.log(
      "Error handling files:",
      error?.response?.data || error.message,
    );
    return undefined;
  }
};

export const deleteProject = async (projectId: string | number) => {
  try {
    const response = await api.post(`${API_URL}/kodesherpa/deleteProject`, {
      projectId,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting project:", error);
    throw error;
  }
};
